import { isAnyOf } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { getMaxDate } from '@/api/dates/utils.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import { selectGetCurrentUser } from '@/api/user/index.ts';
import { forwardAccumulatorApi, matchCheckForwardAccumulatorFulfilled } from '@/store/forwardAccumulator/api/index.ts';
import { getForwardAccumulatorRfsId } from '@/store/forwardAccumulator/selectors.ts';
import { forwardAccumulatorSlice } from '@/store/forwardAccumulator/slice.ts';
import type { FxForwardAccumulator } from '@/store/forwardAccumulator/state.ts';
import { cancelAllForwardAccumulatorRfsThunk } from '@/store/forwardAccumulator/thunk.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { changeClient } from '@/store/tabs/slice.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

const heartbeatListenersByRfsId: Record<string, (rfsId: string) => void> = {};

export const registerRfsForwardAccumulatorListeners = (startAppListening: StartAppListening) => {
  startAppListening({
    matcher: matchCheckForwardAccumulatorFulfilled,
    effect: async (action, api) => {
      const { dispatch, getState } = api;
      const currentState = getState();

      const requestPayload = action.meta.arg.originalArgs;
      const tradeCaptureResponse = action.payload;

      const tileId = requestPayload.tileId;

      const tile = forwardAccumulatorSlice.selectors.getForwardAccumulator(api.getState(), tileId);
      const clientId = selectTileClientId(currentState, tileId);

      if (isNotDefined(tile) || isDefined(tile.execution) || isNotDefined(clientId)) {
        return;
      }

      const { isReadyToPrice, isPriceObsolete, isProductObsolete } = getTradeCaptureStatus(
        tradeCaptureResponse.tradeCapture,
      );

      const currentRfsId = isDefined(tile) ? getForwardAccumulatorRfsId(tile) : undefined;

      // ON A UN CURRENT RFS ID
      if (isDefined(currentRfsId)) {
        if (isReadyToPrice && !isPriceObsolete && !isProductObsolete) {
          // When TC replies ready to price but price/product aren't obsolete, we do nothing
          return;
        }

        if (isProductObsolete && isDefined(currentRfsId)) {
          await dispatch(
            forwardAccumulatorApi.endpoints.cancelForwardAccumulatorRfs.initiate({
              rfsId: currentRfsId,
              tileId,
              removeQuote: true,
            }),
          );
        }
      }
    },
  });

  startAppListening({
    matcher: forwardAccumulatorApi.endpoints.requestForwardAccumulatorRfs.matchFulfilled,
    effect: async (action, api) => {
      const { rfsId, tileId } = action.meta.arg.originalArgs;
      const signalRConnector = getSignalRConnector();

      function onHeartbeatTimeout(id: string) {
        if (id !== rfsId) {
          return;
        }
        api.dispatch(forwardAccumulatorApi.endpoints.cancelForwardAccumulatorRfs.initiate({ rfsId, tileId }));
      }

      heartbeatListenersByRfsId[rfsId] = onHeartbeatTimeout;
      signalRConnector.onHeartbeatTimeout(rfsId, onHeartbeatTimeout);
    },
  });

  startAppListening({
    matcher: forwardAccumulatorApi.endpoints.cancelForwardAccumulatorRfs.matchPending,
    effect: async (action) => {
      const { rfsId } = action.meta.arg.originalArgs;
      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });
  startAppListening({
    matcher: changeClient.match,
    effect: async (_, api) => {
      const { dispatch, getState } = api;
      const state = getState();

      const forwardAccumulator = Object.entries(state.forwardAccumulator);
      if (forwardAccumulator?.length > 0) {
        forwardAccumulator.forEach(async ([tileId, forwardAccumulator]) => {
          const clientId = selectTileClientId(state, tileId);
          const currentUserCache = selectGetCurrentUser(state).data?.clients;
          const maxDate = await getMaxDate(
            clientId,
            currentUserCache,
            dispatch,
            forwardAccumulator.properties.currencyPair,
          );
          dispatch(forwardAccumulatorSlice.actions.setForwardAccumulatorMaxDate({ tileId, maxDate }));
        });
      }

      await api.dispatch(cancelAllForwardAccumulatorRfsThunk());
    },
  });

  startAppListening({
    matcher: forwardAccumulatorApi.endpoints.executeForwardAccumulatorRfs.matchPending,
    effect: async (action) => {
      const { rfsId } = action.meta.arg.originalArgs;
      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });

  startAppListening({
    matcher: isAnyOf(
      forwardAccumulatorSlice.actions.changeClient.match,
      forwardAccumulatorSlice.actions.forwardAccumulatorClientIdUpdated.match,
    ),
    effect: async (action, api) => {
      const tileId =
        forwardAccumulatorSlice.actions.changeClient.match(action) ||
        forwardAccumulatorSlice.actions.forwardAccumulatorClientIdUpdated.match(action)
          ? action.payload.tileId
          : undefined;

      if (isNotDefined(tileId)) {
        return;
      }

      if (
        forwardAccumulatorSlice.actions.changeClient.match(action) &&
        api.getOriginalState().forwardAccumulator[tileId]?.clientId === action.payload.clientId
      ) {
        return;
      }

      const accumulatorTile = forwardAccumulatorSlice.selectors.getForwardAccumulator(api.getState(), tileId);
      if (isNotDefined(accumulatorTile)) {
        return;
      }

      const rfs = accumulatorTile.properties.rfs;

      if (rfs.status === 'AWAITING' || rfs.status === 'PRICING') {
        await api.dispatch(
          forwardAccumulatorApi.endpoints.cancelForwardAccumulatorRfs.initiate({
            tileId: tileId,
            rfsId: rfs.rfsId,
            removeQuote: true,
          }),
        );
      }
    },
  });
};

interface TcData {
  isReadyToPrice: boolean;
  isProductObsolete: boolean;
  isPriceObsolete: boolean;
}

function getTradeCaptureStatus(tradeCapture: FxForwardAccumulator['tradeCapture']): TcData {
  if (tradeCapture?.status !== 'CHECKED') {
    return {
      isReadyToPrice: false,
      isProductObsolete: false,
      isPriceObsolete: false,
    };
  }

  return {
    isReadyToPrice: tradeCapture?.isReadyToPrice,
    isProductObsolete: tradeCapture?.isProductObsolete,
    isPriceObsolete: tradeCapture?.isPriceObsolete,
  };
}
